import { ExternalLink } from 'lucide-react'
import { Button } from 'tailwind-v4/components/button'
import { Page } from 'tailwind-v4/blocks/page'
import { BrandingBrowser } from '@/common/branding-block'
import { useLoyaltyContext } from '@/pages/[org_id]/loyalty/hooks/use-loyalty-context'
import config from '@/config'

/**
 * Live preview of the guest-facing loyalty microsite, in a phone frame.
 *
 * This is where the iframe belongs. It used to sit on the Link Builder, taking
 * the larger half of that screen to show a page the operator wasn't editing —
 * while "Preview Scheme" and "Preview Rewards" in the nav merely opened the
 * real site in a new tab. Now those two nav items land here and the Link
 * Builder is just its form.
 *
 * Venue branding shows INSIDE the frame only: it's a genuine guest-facing
 * preview, so the brand belongs on the canvas, never on the chrome around it.
 */
export default function LoyaltySchemePreviewPage({
  variant = 'sign-up',
}: {
  /** Which microsite route to frame. */
  variant?: 'sign-up' | 'rewards'
}) {
  const { schemeId } = useLoyaltyContext()

  const url = `${config.url.loyalty_end_user}/loyalty-scheme/${schemeId}/${variant}`
  const title = variant === 'rewards' ? 'Preview rewards' : 'Preview scheme'
  const meta =
    variant === 'rewards'
      ? 'The rewards microsite, as a guest sees it.'
      : 'The enrolment page, as a guest sees it.'

  return (
    <Page
      className="new-ui-theme bg-background text-foreground"
      title={title}
      meta={<span>{meta}</span>}
      toolbar={
        <Button
          variant="outline"
          leftIcon={<ExternalLink />}
          onClick={() => {
            window.open(url, '_blank', 'noopener,noreferrer')
          }}
        >
          Open in new tab
        </Button>
      }
    >
      <div className="flex justify-center">
        {/* Capped so the frame scales instead of clipping — the same treatment
            the wi-fi branding preview uses. */}
        <BrandingBrowser
          isMobile
          url={config.url.loyalty_end_user}
          style={{ maxHeight: '100%', maxWidth: '100%' }}
        >
          {/* Fills the frame. `BrandingBrowser`'s mobile variant is a fixed
              393×852 phone, so a fixed-height iframe (it was `h-[600px]`)
              leaves a dead band of empty frame below the content. `flex-1`
              + `min-h-0` takes whatever's left under the status bar. */}
          <iframe className="w-full min-h-0 flex-1" src={url} title={title} />
        </BrandingBrowser>
      </div>
    </Page>
  )
}
